import { useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";

const exportCategories = [
  { key: "patient",       label: "Patient Demographics",     icon: "lucide:user",           description: "Name, DOB, address, contact info",                fhirType: "Patient"            },
  { key: "conditions",    label: "Conditions & Diagnoses",   icon: "lucide:stethoscope",     description: "Active and historical conditions",                 fhirType: "Condition"          },
  { key: "medications",   label: "Medications",              icon: "lucide:pill",           description: "Current and past medications with RxNorm codes",   fhirType: "MedicationRequest"  },
  { key: "labs",          label: "Lab Results",              icon: "lucide:test-tube",      description: "Laboratory results with LOINC codes",              fhirType: "Observation"        },
  { key: "vitals",        label: "Vital Signs",              icon: "lucide:heart-pulse",     description: "Blood pressure, weight, height, BMI",              fhirType: "Observation"        },
  { key: "immunizations", label: "Immunizations",            icon: "lucide:syringe",         description: "Vaccination history",                             fhirType: "Immunization"       },
  { key: "allergies",     label: "Allergies & Intolerances", icon: "lucide:alert-triangle",  description: "Drug and food allergies",                          fhirType: "AllergyIntolerance" },
  { key: "procedures",    label: "Procedures",               icon: "lucide:history",         description: "Surgeries and medical procedures",                 fhirType: "Procedure"          },
  { key: "careplans",     label: "Care Plans",               icon: "lucide:clipboard-list",  description: "Treatment plans and goals",                        fhirType: "CarePlan"           },
  { key: "documents",     label: "Clinical Documents",       icon: "lucide:file-text",       description: "Clinical notes and reports",                       fhirType: "DocumentReference"  },
  { key: "imaging",       label: "Imaging Studies",          icon: "lucide:scan-line",       description: "Radiology reports",                                fhirType: "ImagingStudy"       },
  { key: "encounters",    label: "Encounters",               icon: "lucide:calendar",        description: "Visit history",                                    fhirType: "Encounter"          },
];

function RedIcon({ icon, size = "md" }: { icon: string; size?: "sm" | "md" | "lg" }) {
  return (
    <div className={cn(
      "flex shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50",
      size === "sm" && "h-7 w-7",
      size === "md" && "h-9 w-9",
      size === "lg" && "h-11 w-11"
    )}>
      <Icon icon={icon} className={cn(
        "!text-white",
        size === "sm" && "h-3 w-3",
        size === "md" && "h-4 w-4",
        size === "lg" && "h-5 w-5"
      )} />
    </div>
  );
}

export function FHIRExportSection() {
  const [selectedCategories, setSelectedCategories] = useState<Record<string, boolean>>(
    exportCategories.reduce((acc, cat) => ({ ...acc, [cat.key]: true }), {})
  );
  const [exportFormat, setExportFormat]     = useState<"json" | "ndjson">("json");
  const [bundleType, setBundleType]         = useState<"collection" | "document">("collection");
  const [isExporting, setIsExporting]       = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const selectedCount = Object.values(selectedCategories).filter(Boolean).length;

  const toggleCategory = (key: string) => {
    setSelectedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportProgress(0);
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(r => setTimeout(r, 200));
      setExportProgress(i);
    }
    setIsExporting(false);
  };

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">

      {/* Compliance banner — grey tone */}
      <div className="mb-5 flex items-start gap-4 rounded-3xl border border-[#E5E7EB] bg-white px-5 py-4 shadow-sm">
        <RedIcon icon="lucide:shield" size="sm" />
        <div>
          <p className="text-sm font-extrabold text-[#0A2947]">USCDI v3 Compliant Export</p>
          <p className="mt-0.5 text-xs text-[#4B5563]">
            Your export follows US Core Implementation Guide profiles and uses standard terminologies
            including RxNorm for medications, LOINC for lab results, and SNOMED-CT for conditions.
          </p>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-3">

        {/* Left — category selection + options */}
        <div className="space-y-5 xl:col-span-2">

          {/* Category selection */}
          <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
            <h2 className="mb-1 text-base font-extrabold tracking-tight text-[#0A2947]">
              Select Data to Export
            </h2>
            <p className="mb-4 text-xs text-[#9CA3AF]">
              Choose which health record categories to include in your export
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              {exportCategories.map((category) => {
                const isSelected = selectedCategories[category.key];
                return (
                  <div
                    key={category.key}
                    onClick={() => toggleCategory(category.key)}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-2xl border p-3 text-left transition-all",
                      isSelected
                        ? "border-[#0A2947]/20 bg-[#F7F8FA]"
                        : "border-[#E5E7EB] hover:border-[#0A2947]/20"
                    )}
                  >
                    {/* Checkbox */}
                    <div className={cn(
                      "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                      isSelected ? "border-[#0A2947] bg-[#0A2947]" : "border-[#D1D5DB]"
                    )}>
                      {isSelected && (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                          stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                          className="h-3 w-3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Icon icon={category.icon} className={cn(
                          "h-3.5 w-3.5 shrink-0",
                          isSelected ? "text-[#0A2947]" : "text-[#9CA3AF]"
                        )} />
                        <span className={cn(
                          "text-sm font-extrabold",
                          isSelected ? "text-[#0A2947]" : "text-[#6B7280]"
                        )}>
                          {category.label}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-xs text-[#9CA3AF]">{category.description}</p>
                      <span className="mt-1 inline-block rounded-md border border-[#E5E7EB] bg-[#F9FAFB] px-1.5 py-0.5 text-[10px] font-semibold text-[#6B7280]">
                        {category.fhirType}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  const allSelected = selectedCount === exportCategories.length;
                  const newState: Record<string, boolean> = {};
                  exportCategories.forEach(c => { newState[c.key] = !allSelected; });
                  setSelectedCategories(newState);
                }}
                className="!h-auto !rounded-lg !border-[#E5E7EB] !px-3 !py-1.5 !text-xs !font-semibold !text-[#4B5563] hover:!border-[#0A2947] hover:!text-[#0A2947]"
              >
                {selectedCount === exportCategories.length ? "Deselect All" : "Select All"}
              </Button>
              <span className="text-xs text-[#9CA3AF]">
                {selectedCount} of {exportCategories.length} selected
              </span>
            </div>
          </div>

          {/* Export options */}
          <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-base font-extrabold tracking-tight text-[#0A2947]">Export Options</h2>

            {/* Format */}
            <div className="mb-4">
              <p className="mb-2 text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">Format</p>
              <div className="flex gap-3">
                {([
                  { key: "json",   label: "JSON Bundle",   icon: "lucide:file-json" },
                  { key: "ndjson", label: "NDJSON (Bulk)", icon: "lucide:file-text" },
                ] as const).map(({ key, label, icon }) => (
                  <Button
                    key={key}
                    variant="outline"
                    onClick={() => setExportFormat(key)}
                    className={cn(
                      "!flex !h-8 !items-center !gap-2 !rounded-full !px-4 !text-xs !font-bold",
                      exportFormat === key
                        ? "!border-[#F36F8F] !bg-[#F36F8F] !text-white hover:!border-[#F36F8F] hover:!bg-[#F36F8F] hover:!text-white"
                        : "!border-[#E5E7EB] !bg-[#F3F4F6] !text-[#111827] hover:!border-[#D1D5DB] hover:!bg-white hover:!text-[#111827]"
                    )}
                  >
                    <Icon icon={icon} className={cn("h-3.5 w-3.5", exportFormat === key ? "!text-white" : "text-[#9CA3AF]")} />
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Bundle type */}
            <div>
              <p className="mb-2 text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">Bundle Type</p>
              <div className="flex gap-3">
                {([
                  { key: "collection", label: "Collection", desc: "Simple bundle of resources" },
                  { key: "document",   label: "Document",   desc: "Signed clinical document"   },
                ] as const).map(({ key, label, desc }) => (
                  <Button
                    key={key}
                    variant="outline"
                    onClick={() => setBundleType(key)}
                    className={cn(
                      "!h-auto !flex-1 !rounded-2xl !px-4 !py-3 !text-left",
                      bundleType === key
                        ? "!border-[#0A2947] !bg-[#F7F8FA] hover:!border-[#0A2947] hover:!bg-[#F7F8FA]"
                        : "!border-[#E5E7EB] hover:!border-[#0A2947]/20"
                    )}
                  >
                    <p className={cn(
                      "text-sm font-extrabold",
                      bundleType === key ? "text-[#0A2947]" : "text-[#6B7280]"
                    )}>
                      {label}
                    </p>
                    <p className="mt-0.5 text-xs text-[#9CA3AF]">{desc}</p>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right — export summary */}
        <div>
          <div className="sticky top-5 rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-base font-extrabold tracking-tight text-[#0A2947]">Export Summary</h2>

            {/* Summary rows */}
            <div className="mb-4 space-y-2.5">
              {[
                { label: "Categories",  value: String(selectedCount) },
                { label: "Format",      value: exportFormat.toUpperCase() },
                { label: "Bundle Type", value: bundleType.charAt(0).toUpperCase() + bundleType.slice(1) },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-xs text-[#9CA3AF]">{label}</span>
                  <span className="text-xs font-extrabold text-[#0A2947]">{value}</span>
                </div>
              ))}
            </div>

            {/* FHIR resource tags — red/50 border */}
            <div className="mb-4 rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-3">
              <p className="mb-2 text-[10px] font-extrabold uppercase tracking-wide text-[#9CA3AF]">
                FHIR Resources
              </p>
              <div className="flex flex-wrap gap-1.5">
                {exportCategories
                  .filter(c => selectedCategories[c.key])
                  .map(c => (
                    <span
                      key={c.key}
                      className="rounded-md border px-1.5 py-0.5 text-[10px] font-semibold text-[#6B7280]"
                      style={{ borderColor: "rgba(228,0,43,0.5)" }}
                    >
                      {c.fhirType}
                    </span>
                  ))}
              </div>
            </div>

            {/* Compliance checks */}
            <div className="mb-5 space-y-2">
              {["FHIR R4 Format", "US Core Profiles", "Standard Terminologies"].map(item => (
                <div key={item} className="flex items-center gap-2">
                  <Icon icon="lucide:check-circle-2" className="h-3.5 w-3.5 shrink-0 text-[#0A2947]" />
                  <span className="text-xs font-medium text-[#4B5563]">{item}</span>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            {isExporting && (
              <div className="mb-4">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs text-[#9CA3AF]">Exporting...</span>
                  <span className="text-xs font-extrabold text-[#0A2947]">{exportProgress}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                  <div
                    className="h-2 rounded-full bg-[#0A2947] transition-all duration-200"
                    style={{ width: `${exportProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Export button */}
           <div className="flex justify-center">
            <Button
              onClick={handleExport}
              disabled={selectedCount === 0 || isExporting}
              loading={isExporting}
              className="!inline-flex !h-auto !items-center !justify-center !gap-2 !rounded-xl !bg-[#0A2947] !px-5 !py-2 !text-xs !font-extrabold !text-white hover:!bg-[#0A2947]"
            >
              {isExporting ? (
                "Exporting..."
              ) : (
                <>
                  <Icon icon="lucide:download" className="h-4 w-4 !text-white" />
                  Export FHIR Bundle
                </>
              )}
            </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
